/**
 * DemoGate Component
 *
 * The app's access + routing gate, wrapped around the app at App.js. It decides
 * which entry a visitor needs: handle a special path directly (short link,
 * /clear, permanent URL, chat link), show the OTP screen (PWA sign-in), or pass
 * straight through to the app.
 *
 * The shared-password screen it used to show was retired (password-gate-retire,
 * 2026-10-11): sign-up is enforced server-side (migration 041), the admin
 * dashboard is behind can_view_dashboard() (042), and the SMS send-gate protects
 * the code-send path. See docs/core/auth.md § DemoGate Check Order.
 *
 * Flow:
 * - Profile users (profile_user_id in localStorage) always pass through
 * - Special paths (/l/, /clear, /u/, chatRoomId URLs) handled directly
 * - PWA context without profile → OTP screen (delegates to PWAAuthScreen)
 * - Everyone else → through to the app
 */

import { useState, useEffect } from 'react';
import { hideInstantSplash } from '../../utils/instantSplash';
import { isDemoModeEnabled } from '../../services/auth/demoAuth';
import { getAuthSession, hydrateFromAuth } from '../../services/auth/otpAuth';
import { supabase } from '../../services/supabase';
import { logEvent } from '../../services/analytics/eventLogger';
import {
  getStoredTier,
  setStoredTier,
  tierFromAccountStatus,
  isMergedAccountStatus,
  isProfileWorld,
} from '../../utils/userTier';
import { getProfileUserId } from '../../utils/identityUtils';
import { setSessionChatRoomId } from '../../services/session/sessionManager';
import PWAAuthScreen from './PWAAuthScreen';
import { isPWA } from '../../utils/installPrompt';

const TIER_SEED_TIMEOUT_MS = 4000;

/**
 * First open after the one-record tier shipped (auth.md § Account Tiers): a
 * profile install with no stored tier copy reads its own account_status once,
 * before the app shows, and stores it. A failed read leaves no copy — the
 * tier module uses the old session reading for this session only — and is
 * logged; the next open retries. (Mark, 2026-10-05.)
 */
const seedAccountTierIfMissing = async () => {
  if (!isProfileWorld() || getStoredTier()) return;
  const profileUserId = getProfileUserId();

  let reason = null;
  let timer;
  try {
    // Tier only, through profile_tier_check (open-access lockdown) — works
    // before sign-in resolves and when another session owns the row.
    const read = supabase
      .rpc('profile_tier_check', { p_profile_user_id: profileUserId })
      .maybeSingle();
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => resolve({ timedOut: true }), TIER_SEED_TIMEOUT_MS);
    });
    const { data, error, timedOut } = await Promise.race([read, timeout]);
    clearTimeout(timer);
    // A merged (retired) Simple profile has no tier to seed — the takeover
    // (useConversionCheck) owns this boot. Not a seed failure. (039)
    if (isMergedAccountStatus(data?.account_status)) return;
    const tier = tierFromAccountStatus(data?.account_status);
    if (tier) {
      setStoredTier(tier);
      console.log('[DEMO_GATE] Tier copy seeded from the database:', tier);
      return;
    }
    reason = timedOut ? 'timeout' : error ? error.message : 'no_profile_row';
  } catch (e) {
    clearTimeout(timer);
    reason = e?.message || 'exception';
  }
  console.warn('[DEMO_GATE] Tier seed failed — session reading for this session:', reason);
  logEvent('tier_seed_fallback', { profile_user_id: profileUserId, reason });
};

export const DemoGate = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [showPWAAuth, setShowPWAAuth] = useState(false);

  // Decide the entry on mount
  useEffect(() => {
    const checkSession = async () => {
      // 0. Keyless profile install → seed the tier copy before anything shows
      await seedAccountTierIfMissing();

      // 1. Demo mode disabled → allow (routing/OTP off; local dev)
      if (!isDemoModeEnabled()) {
        setIsChecking(false);
        return;
      }

      // 2. Short link redirect in progress: /l/{id}
      if (window.location.pathname.match(/^\/l\/.+$/)) {
        console.log('[DEMO_GATE] Short link redirect in progress, waiting...');
        return; // Keep isChecking true — show nothing until redirect completes
      }

      // 3. /clear → allow (testing utility)
      if (window.location.pathname === '/clear') {
        console.log('[DEMO_GATE] /clear page, allowing access');
        setIsChecking(false);
        return;
      }

      // 4. Root / → allow (welcome page, public path)
      // The welcome page is public — anyone at / without a profile sees it.
      // Skip if: signup_initiated, pending_otp_required (mid-signup users
      // continue to OTP), or PWA context (fall through to step 8 for OTP).
      if (
        window.location.pathname === '/' &&
        !localStorage.getItem('profile_user_id') &&
        !isPWA() &&
        !sessionStorage.getItem('signup_initiated') &&
        !sessionStorage.getItem('pending_otp_required') &&
        !new URLSearchParams(window.location.search).has('screen') &&
        !new URLSearchParams(window.location.search).has('s')
      ) {
        console.log('[DEMO_GATE] Root without profile, allowing through to welcome page');
        setIsChecking(false);
        return;
      }

      // 5. /u/:profileUserId → allow (permanent URL QR scan)
      const pathname = window.location.pathname;
      const permanentUrlMatch = pathname.match(/^\/u\/([a-zA-Z0-9_-]+)$/);
      if (permanentUrlMatch) {
        console.log(
          '[DEMO_GATE] Permanent URL detected (/u/' + permanentUrlMatch[1] + '), allowing access'
        );
        setIsChecking(false);
        return;
      }

      // 6. Has chatRoomId in URL → init anon session + allow (no visit limits)
      const urlParams = new URLSearchParams(window.location.search);
      const chatRoomIdFromParam = urlParams.get('chatRoomId') || urlParams.get('c');

      // Also check for simple format: ?chat_123456789_abcdef
      const search = window.location.search;
      let chatRoomIdFromSimpleFormat = null;
      if (search.startsWith('?') && search.length > 1) {
        const potential = search.substring(1);
        if (potential.startsWith('chat_')) {
          chatRoomIdFromSimpleFormat = potential;
        }
      }

      const chatRoomId = chatRoomIdFromParam || chatRoomIdFromSimpleFormat;

      if (chatRoomId) {
        console.log('[DEMO_GATE] ChatRoomId in URL, initializing anon session');
        // Initialize session for anon user
        setSessionChatRoomId(chatRoomId, true);
        sessionStorage.setItem('user_type', 'anon');
        console.log('[DEMO_GATE] Session initialized for anon:', chatRoomId);
        setIsChecking(false);
        return;
      }

      // 7. Pending OTP required — either from SignupPage phone confirmation
      // or from UUID mismatch detection in ensureProfileSynced().
      // Keep the flag set — hydrateFromAuth removes it after OTP completes.
      // Removing it here would let stray reloads bypass back to the app,
      // where ensureProfileSynced detects the mismatch again → reload loop.
      if (sessionStorage.getItem('pending_otp_required') === 'true') {
        console.log('[DEMO_GATE] Pending OTP required, showing auth screen');
        setShowPWAAuth(true);
        setIsChecking(false);
        return;
      }

      // 8. PWA + no profile_user_id → check for auth session or show OTP screen
      if (isPWA() && !localStorage.getItem('profile_user_id')) {
        console.log('[DEMO_GATE] PWA context without profile_user_id');

        // Check for existing Supabase auth session (silent recovery)
        const session = await getAuthSession();
        if (session) {
          console.log('[DEMO_GATE] Existing auth session found, hydrating silently');
          // hydrateFromAuth calls window.location.reload() on success
          const result = await hydrateFromAuth(session);
          if (result.error) {
            console.log('[DEMO_GATE] Silent hydration failed:', result.error);
            // Fall through to show PWA auth screen
          } else {
            return; // reload happening
          }
        }

        // No session or silent hydration failed → show OTP screen
        console.log('[DEMO_GATE] Showing PWA auth screen');
        setShowPWAAuth(true);
        setIsChecking(false);
        return;
      }

      // 9. Everyone else → through to the app (the shared-password gate was retired)
      console.log('[DEMO_GATE] Allowing access');
      setIsChecking(false);
    };

    checkSession();
  }, []);

  // The OTP auth screen renders OUTSIDE ScreenRouter, so its splash-hide effect
  // never fires here — hide the index.html splash ourselves the moment the OTP
  // screen is about to show. While isChecking, the splash stays up and covers
  // the check; when we render children, ScreenRouter hides the splash.
  useEffect(() => {
    if (showPWAAuth) {
      hideInstantSplash();
    }
  }, [showPWAAuth]);

  // Show nothing while checking (prevents flash of content)
  if (isChecking) {
    return null;
  }

  // PWA auth screen (OTP flow)
  if (showPWAAuth) {
    return <PWAAuthScreen />;
  }

  // Through to the app
  return <>{children}</>;
};
